import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  SerpData,
  SerpItem,
  UpdateSerpPayload
} from '@/app/Serp/types/serpApiTypes';

const serpDataSlice = createSlice({
  name: 'serpData',
  initialState: {} as SerpData,
  reducers: {
    setSerp: (_state, { payload }: PayloadAction<SerpData | null>) => {
      if (payload == null) {
        return {};
      }

      if (
        payload.analysis_serp?.results_types?.length ||
        !payload.items?.length
      ) {
        return payload;
      }

      const counts = new Map<string, number>();
      for (const item of payload.items) {
        const name = item.fa_type || 'نامشخص';
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }

      return {
        ...payload,
        analysis_serp: {
          ...payload.analysis_serp,
          results_types: [...counts].map(([name, y]) => ({ name, y }))
        }
      };
    },
    updateSerp: (state, { payload }: PayloadAction<UpdateSerpPayload>) => {
      Object.entries(payload).forEach(([key, value]) => {
        if (key === 'domains') {
          const domains = value as Record<string, Partial<SerpItem>>;
          state.items?.forEach(item => {
            if (item.type === 'organic' && domains[item.url]) {
              const domainUpdate = domains[item.url];
              if ('host_location' in domainUpdate) {
                const location = domainUpdate.host_location;
                item.host_location =
                  location == null || location === '-1' || location === '-'
                    ? null
                    : location;
                item.host_company = domainUpdate.host_company;
                item.host_company_host = domainUpdate.host_company_host;
                item.host_company_website = domainUpdate.host_company_website;
                item.domain_age = domainUpdate.domain_age;
                item.domain_rank = domainUpdate.domain_rank;
                item.domain_suffix = domainUpdate.domain_suffix;
                item.domain_expiration = domainUpdate.domain_expiration;
                item.moz_domain_authority = domainUpdate.moz_domain_authority;
                item.moz_spam_score = domainUpdate.moz_spam_score;
              }
              if ('num_chars' in domainUpdate) {
                item.num_chars = domainUpdate.num_chars;
                item.num_h1 = domainUpdate.num_h1;
                item.num_h2 = domainUpdate.num_h2;
                item.num_h3 = domainUpdate.num_h3;
                item.num_images = domainUpdate.num_images;
                item.num_paragraphs = domainUpdate.num_paragraphs;
                item.num_sentences = domainUpdate.num_sentences;
                item.num_unique_words = domainUpdate.num_unique_words;
                item.num_words = domainUpdate.num_words;
                item.page_modified_at = domainUpdate.page_modified_at;
                item.page_published_at = domainUpdate.page_published_at;
                item.page_status_code = domainUpdate.page_status_code;
                item.page_title = domainUpdate.page_title;
                item.study_duration = domainUpdate.study_duration;
              }
            }
          });
        } else if (!(key in state)) {
          (state as Record<string, unknown>)[key] = value;
        } else if (typeof value === 'object' && value !== null) {
          const current = (state as Record<string, unknown>)[key];
          if (current == null || typeof current !== 'object') {
            (state as Record<string, unknown>)[key] = {
              ...(value as Record<string, unknown>)
            };
          } else {
            Object.entries(value as Record<string, unknown>).forEach(
              ([innerKey, innerValue]) => {
                (current as Record<string, unknown>)[innerKey] = innerValue;
              }
            );
          }
        }
      });
    }
  }
});

export const { setSerp, updateSerp } = serpDataSlice.actions;
export default serpDataSlice;
